import { OnboardingHubEvent } from '../enum/enums';
import {
  AppStepReviewedByRole,
  AppStepReviewStatus,
  RawOnboardingHubPayload,
  RawReviewedEntity,
  StepReviewState,
  StepReviewUpdate,
} from './onboarding-notification.interfaces';
import { resolveGuarantorWorkflowStepRoute, resolveStepRoute } from './entity-step-mapping';

const REVIEW_EVENTS = new Set<string>([
  OnboardingHubEvent.EntityRejected,
  OnboardingHubEvent.EntityVerified,
  OnboardingHubEvent.BulkEntitiesRejected,
  OnboardingHubEvent.BulkEntitiesVerified,
]);

export function isReviewNotification(raw: unknown): raw is RawOnboardingHubPayload {
  if (!raw || typeof raw !== 'object') return false;
  const obj = raw as Record<string, unknown>;
  return (
    typeof obj['notificationTypeName'] === 'string' &&
    REVIEW_EVENTS.has(obj['notificationTypeName'])
  );
}

function deriveState(notificationTypeName: string): StepReviewState {
  switch (notificationTypeName) {
    case OnboardingHubEvent.EntityVerified:
    case OnboardingHubEvent.BulkEntitiesVerified:
      return 'approved';
    case OnboardingHubEvent.EntityRejected:
    case OnboardingHubEvent.BulkEntitiesRejected:
      return 'objected';
    default:
      return 'none';
  }
}

function extractEntities(raw: RawOnboardingHubPayload): RawReviewedEntity[] {
  const entities = raw.reviewDetail?.entities;
  return Array.isArray(entities) ? entities : [];
}

export function mapNotificationToStepUpdates(
  raw: unknown,
): StepReviewUpdate[] {
  if (!isReviewNotification(raw)) return [];

  const state = deriveState(raw.notificationTypeName);
  if (state === 'none') return [];

  const applicationId = raw.applicationId ?? 0;
  const reviewedByRole =
    raw.reviewDetail?.reviewedByRole != null && !Number.isNaN(Number(raw.reviewDetail.reviewedByRole))
      ? Number(raw.reviewDetail.reviewedByRole)
      : null;
  const reviewedBy = raw.reviewDetail?.reviewedByRoleName ?? raw.performedByUserName ?? '';
  const performedAt = raw.performedAtUTC ?? '';

  const updates: StepReviewUpdate[] = [];

  for (const entity of extractEntities(raw)) {
    const stepRoute = resolveStepRoute(entity.entityName);
    if (!stepRoute) continue;

    updates.push({
      applicationId,
      stepRoute,
      state,
      comment: entity.comment ?? null,
      entityName: entity.entityName,
      entityId: entity.entityId ?? 0,
      reviewedByRole,
      reviewedBy,
      performedAt,
    });
  }

  return updates;
}

/** Same as {@link mapNotificationToStepUpdates} but maps guarantor review entities to `guarantors-workflow/*` routes. */
export function mapNotificationToGuarantorWorkflowStepUpdates(raw: unknown): StepReviewUpdate[] {
  if (!isReviewNotification(raw)) return [];

  const state = deriveState(raw.notificationTypeName);
  if (state === 'none') return [];

  const applicationId = Number(raw.applicationId) || 0;
  const reviewedByRole =
    raw.reviewDetail?.reviewedByRole != null && !Number.isNaN(Number(raw.reviewDetail.reviewedByRole))
      ? Number(raw.reviewDetail.reviewedByRole)
      : null;
  const reviewedBy = raw.reviewDetail?.reviewedByRoleName ?? raw.performedByUserName ?? '';
  const performedAt = raw.performedAtUTC ?? '';

  const updates: StepReviewUpdate[] = [];

  for (const entity of extractEntities(raw)) {
    const stepRoute = resolveGuarantorWorkflowStepRoute(entity.entityName);
    if (!stepRoute) continue;

    updates.push({
      applicationId,
      stepRoute,
      state,
      comment: entity.comment ?? null,
      entityName: entity.entityName,
      entityId: entity.entityId ?? 0,
      reviewedByRole,
      reviewedBy,
      performedAt,
    });
  }

  return updates;
}

export function applyStepUpdates(
  current: AppStepReviewStatus,
  updates: readonly StepReviewUpdate[],
): AppStepReviewStatus {
  if (updates.length === 0) return current as Record<number, Record<string, StepReviewState>>;

  const batchByApp = new Map<number, Map<string, StepReviewState>>();
  for (const u of updates) {
    let stepMap = batchByApp.get(u.applicationId);
    if (!stepMap) {
      stepMap = new Map();
      batchByApp.set(u.applicationId, stepMap);
    }
    const existing = stepMap.get(u.stepRoute);
    if (u.state === 'objected' || existing !== 'objected') {
      stepMap.set(u.stepRoute, u.state);
    }
  }

  const next = { ...current } as Record<number, Record<string, StepReviewState>>;
  for (const [appId, stepMap] of batchByApp) {
    const appSteps = { ...(next[appId] ?? {}) };
    for (const [route, state] of stepMap) {
      appSteps[route] = state;
    }
    next[appId] = appSteps;
  }

  return next;
}

export function applyReviewedByRoleUpdates(
  current: AppStepReviewedByRole,
  updates: readonly StepReviewUpdate[],
): AppStepReviewedByRole {
  if (updates.length === 0) return current as Record<number, Record<string, number>>;

  const next = { ...current } as Record<number, Record<string, number>>;
  for (const update of updates) {
    if (update.reviewedByRole == null) continue;
    const appRoles = { ...(next[update.applicationId] ?? {}) };
    appRoles[update.stepRoute] = update.reviewedByRole;
    next[update.applicationId] = appRoles;
  }

  return next;
}
